(function () {
  /** @typedef {'monthly' | 'quarterly'} SubscriptionPlanMode */
  /** @typedef {SubscriptionPlanMode | 'one-time'} PurchaseMode */

  if (window.BBCOSubscriptionBuyBox && typeof window.BBCOSubscriptionBuyBox.init === 'function') {
    window.BBCOSubscriptionBuyBox.init(document);
    return;
  }

  /**
   * Initializes one shared subscription buy box and keeps the product form's
   * variant and selling plan in sync with the selected card.
   *
   * Expected markup (rendered by snippets/subscription-buy-box.liquid):
   * - widget dataset: defaultPlan, otpBehavior, selectedPlan (PurchaseMode, set here)
   * - [data-purchase-card] buttons with dataset: purchaseCard, variantId, sellingPlanId, selectionEvent
   * - [data-subscription-buy-box-otp] button with dataset: variantId, selectionEvent
   * - select[data-subscription-buy-box-selling-plan] inside the same form
   *
   * @param {HTMLElement} widget Element marked with data-subscription-buy-box.
   * @returns {void}
   */
  function initWidget(widget) {
    if (!widget || widget.dataset.initialized === 'true') return;

    /** @type {HTMLFormElement | null} */
    const form = widget.closest('form');
    if (!form) return;

    /** @type {HTMLSelectElement | null} Native variant select; its options are an HTMLOptionsCollection. */
    const variantSelect = form.querySelector('select[name="id"]');
    /** @type {HTMLButtonElement | null} */
    const addToCartButton = form.querySelector('[name="add"]');
    /** @type {HTMLButtonElement[]} Subscription card buttons, in DOM order. */
    const planTriggers = Array.from(widget.querySelectorAll('[data-purchase-card]'));
    /** @type {HTMLButtonElement | null} */
    const otpButton = widget.querySelector('[data-subscription-buy-box-otp]');
    /** @type {SubscriptionPlanMode} */
    const defaultPlan = widget.dataset.defaultPlan || 'quarterly';
    let hasInteracted = false;

    if (!variantSelect || !addToCartButton || !planTriggers.length || !otpButton) return;

    widget.dataset.initialized = 'true';

    /**
     * Sets the selling-plan select in the product form.
     *
     * @param {string | undefined} planId Shopify selling plan ID; empty or undefined means one-time purchase.
     * @returns {void}
     */
    function setSellingPlan(planId) {
      /** @type {HTMLSelectElement | null} */
      const sellingPlanControl = form.querySelector('[data-subscription-buy-box-selling-plan]');
      if (!sellingPlanControl) return;

      // Setting .value selects the matching option and deselects the others.
      sellingPlanControl.value = planId || '';
    }

    /**
     * Restores the variant and selling plan for the selected card, in case
     * theme or app scripts changed the form.
     *
     * @returns {void}
     */
    function syncSelectedPurchase() {
      /** @type {PurchaseMode | undefined} */
      const selectedMode = widget.dataset.selectedPlan;
      /** @type {HTMLButtonElement | null} */
      const selectedTrigger = widget.querySelector('[data-purchase-card="' + selectedMode + '"]');
      const expectedVariantId = selectedMode === 'one-time'
        ? otpButton.dataset.variantId
        : selectedTrigger && selectedTrigger.dataset.variantId;

      if (expectedVariantId && variantSelect.value !== expectedVariantId) {
        setVariant(expectedVariantId);
      }
      setSellingPlan(selectedTrigger ? selectedTrigger.dataset.sellingPlanId : '');
    }

    /**
     * Re-syncs a few times after a selection, because theme and app scripts
     * may update the form asynchronously.
     *
     * @returns {void}
     */
    function scheduleSelectedPurchaseSync() {
      window.setTimeout(syncSelectedPurchase, 0);
      window.setTimeout(syncSelectedPurchase, 500);
      window.setTimeout(syncSelectedPurchase, 1500);
    }

    /**
     * Selects a variant in the native select and notifies the theme.
     * Also moves the selected attribute, because the theme's option selector
     * reads [selected] when it initializes.
     *
     * @param {string | undefined} variantId Shopify variant ID.
     * @returns {void}
     */
    function setVariant(variantId) {
      if (!variantId) return;

      const normalizedVariantId = variantId.toString();
      /** @type {HTMLOptionElement | null} */
      let matchingOption = null;

      for (const option of variantSelect.options) {
        const isMatch = option.value === normalizedVariantId;
        option.defaultSelected = isMatch;
        option.selected = isMatch;
        if (isMatch) matchingOption = option;
      }

      /** @type {HTMLSelectElement | null} Option dropdown built by the theme's Shopify.OptionSelectors. */
      const optionSelector = form.querySelector('.single-option-selector');

      if (matchingOption && optionSelector) {
        optionSelector.value = matchingOption.text;
        optionSelector.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      variantSelect.value = normalizedVariantId;
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Pushes an Intelligems event.
     *
     * @param {string | undefined} eventName Event name from the button's data-selection-event.
     * @returns {void}
     */
    function trackSelection(eventName) {
      if (!eventName) return;
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: eventName });
    }

    /**
     * Updates one card's radio state, highlight, and benefit details.
     *
     * @param {HTMLButtonElement} trigger Card button with data-purchase-card.
     * @param {boolean} isSelected Whether the card is the selected plan.
     * @param {boolean} isTabbable Whether the card receives focus when tabbing into the radio group.
     * @returns {void}
     */
    function updateCard(trigger, isSelected, isTabbable) {
      const card = trigger.closest('[data-plan-card]');
      /** @type {HTMLElement | null} */
      const details = card ? card.querySelector('[data-plan-details]') : null;

      trigger.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      trigger.setAttribute('tabindex', isTabbable ? '0' : '-1');
      if (card) card.classList.toggle('is-selected', isSelected);
      if (details) details.hidden = !isSelected;
    }

    /**
     * Selects a subscription card and updates its UI and form state.
     *
     * @param {SubscriptionPlanMode} mode Subscription plan to select.
     * @param {boolean} shouldTrack Whether to push the card's Intelligems event.
     * @returns {void}
     */
    function selectSubscription(mode, shouldTrack) {
      for (const trigger of planTriggers) {
        const isSelected = trigger.dataset.purchaseCard === mode;
        updateCard(trigger, isSelected, isSelected);

        if (isSelected) {
          setVariant(trigger.dataset.variantId);
          setSellingPlan(trigger.dataset.sellingPlanId);
          if (shouldTrack) trackSelection(trigger.dataset.selectionEvent);
        }
      }

      otpButton.setAttribute('aria-pressed', 'false');
      widget.dataset.selectedPlan = mode;
      scheduleSelectedPurchaseSync();
    }

    for (const [triggerIndex, trigger] of planTriggers.entries()) {
      trigger.addEventListener('click', function () {
        hasInteracted = true;
        selectSubscription(trigger.dataset.purchaseCard, true);
      });

      trigger.addEventListener('keydown', function (event) {
        let nextIndex = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (triggerIndex + 1) % planTriggers.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (triggerIndex - 1 + planTriggers.length) % planTriggers.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = planTriggers.length - 1;
        }

        if (nextIndex === null) return;

        event.preventDefault();
        hasInteracted = true;
        selectSubscription(planTriggers[nextIndex].dataset.purchaseCard, true);
        planTriggers[nextIndex].focus();
      });
    }

    otpButton.addEventListener('click', function () {
      hasInteracted = true;

      // No card is selected, so the first card stays tabbable.
      for (const [triggerIndex, trigger] of planTriggers.entries()) {
        updateCard(trigger, false, triggerIndex === 0);
      }

      setVariant(otpButton.dataset.variantId);
      setSellingPlan('');
      otpButton.setAttribute('aria-pressed', 'true');
      widget.dataset.selectedPlan = 'one-time';
      scheduleSelectedPurchaseSync();
      trackSelection(otpButton.dataset.selectionEvent);

      if (widget.dataset.otpBehavior !== 'select_only') {
        addToCartButton.click();
      }
    });

    selectSubscription(defaultPlan, false);

    form.addEventListener('click', syncSelectedPurchase, true);
    form.addEventListener('submit', syncSelectedPurchase, true);

    /**
     * Re-applies the default plan after the theme's own variant setup on
     * window load, unless the shopper has already chosen.
     *
     * @returns {void}
     */
    function syncDefaultAfterThemeInit() {
      if (!hasInteracted) {
        selectSubscription(defaultPlan, false);
      }
    }

    if (document.readyState === 'complete') {
      window.setTimeout(syncDefaultAfterThemeInit, 0);
    } else {
      window.addEventListener('load', syncDefaultAfterThemeInit, { once: true });
    }
  }

  /**
   * Initializes every shared subscription buy box within a document or section.
   *
   * @param {Document | Element} root Root node to search.
   * @returns {void}
   */
  function initAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches('[data-subscription-buy-box]')) initWidget(scope);
    for (const widget of scope.querySelectorAll('[data-subscription-buy-box]')) {
      initWidget(widget);
    }
  }

  window.BBCOSubscriptionBuyBox = window.BBCOSubscriptionBuyBox || {};
  window.BBCOSubscriptionBuyBox.init = initAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
